// @flow

import React, { Fragment } from 'react';
import type { Node } from 'react';
import parseHtmlToReactComponent from 'html-react-parser';
import { renderToStaticMarkup } from 'react-dom/server';
import { find } from '@riseart/fe-utils';

/**
 * HeadContentService
 *
 * Service to attach and detach any raw tags to head of the page.
 * It has SSR support and allows to stringify and extract data as raw html
 */
export const HeadContentService: Object = {
  data: [],
  ssr: false,
  markerMetaName: 'riseart-head-content-injection',
  startMarker: 'start',
  endMarker: 'end',

  /**
   * config
   *
   * @param {Array<Object>} initialData
   * @param {boolean} ssr
   */
  config(initialData: Array<Object> = [], ssr: boolean = false) {
    this.data = initialData ? [...initialData] : [];
    this.ssr = ssr;
  },
  /**
   * add
   *
   * @param {Object} data
   * @returns {void}
   */
  add(data: Object): void {
    const normalizedData = this.normalizeData(renderToStaticMarkup(data));

    if (!find(this.data, (item) => item.data === normalizedData)) {
      this.data.push({ attached: false, data: normalizedData });
      this.attach();
    }
  },
  /**
   * reset
   *
   * @returns {void}
   */
  reset(): void {
    this.detach();
    this.data = [];
  },
  /**
   * get
   *
   * @returns {Array<Object>}
   */
  get(): Array<Object> {
    return this.data;
  },

  /**
   * getElements
   *
   * @returns {Node}
   */
  getElements(): Node {
    return (
      <Fragment>
        <meta name={this.markerMetaName} content={this.startMarker} />
        {parseHtmlToReactComponent(this.stringifyTags())}
        <meta name={this.markerMetaName} content={this.endMarker} />
      </Fragment>
    );
  },

  /**
   * stringify
   *
   * @returns {string}
   */
  stringify(): string {
    const stringifiedContent = this.stringifyTags();

    return stringifiedContent
      ? `<meta name="${this.markerMetaName}" content="${this.startMarker}">${stringifiedContent}<meta name="${this.markerMetaName}" content="${this.endMarker}">`
      : '';
  },

  /**
   * stringifyTags
   *
   * @returns {string}
   */
  stringifyTags(): string {
    if (!this.data || !this.data.length) {
      return '';
    }
    let stringifiedItems = '';
    this.data = this.data.map((item) => {
      if (item.attached) {
        return item;
      }

      stringifiedItems += item.data;
      return { ...item, attached: true };
    });

    return stringifiedItems;
  },

  /**
   * attachNodes
   *
   * @param {Element} parent
   * @returns {void}
   */
  attachNodes(parent: Element): void {
    const content = this.stringifyTags();

    if (!content || !parent) {
      return;
    }

    // Append start marker
    parent.append(
      this.createMarkerNode(
        'meta',
        [
          { name: 'name', value: this.markerMetaName },
          { name: 'content', value: this.startMarker },
        ],
        parent,
      ),
    );

    // Append content between markers
    const nodes = this.parseHtml(content);
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];

      // Create script tags manually, because otherwise the scripts will not be executed
      if (node.nodeName === 'SCRIPT') {
        const element = document.createElement('script');

        // Set script attributes
        if (node.hasAttributes()) {
          const { attributes } = node;

          for (let i = attributes.length - 1; i >= 0; i -= 1) {
            element.setAttribute(attributes[i].name, attributes[i].value);
          }
        }

        element.innerHTML = node.innerHTML;
        parent.appendChild(element);
      } else {
        parent.appendChild(node);
      }
    }

    // Append end marker
    parent.append(
      this.createMarkerNode(
        'meta',
        [
          { name: 'name', value: this.markerMetaName },
          { name: 'content', value: this.endMarker },
        ],
        parent,
      ),
    );
  },
  /**
   * attach
   *
   * @returns {void}
   */
  attach(): void {
    if (this.ssr) {
      return;
    }

    this.attachNodes(document.querySelector('head'));
  },
  /**
   * detach
   *
   * @returns {void}
   */
  detach(): void {
    if (this.ssr) {
      return;
    }

    let shouldDeleteNode = false;
    const head = document.querySelector('head');

    head &&
      [...head.childNodes].forEach((node: Object) => {
        if (node.name === this.markerMetaName && node.content === this.startMarker) {
          shouldDeleteNode = true;
        }

        if (shouldDeleteNode) {
          head.removeChild(node);
        }

        if (node.name === this.markerMetaName && node.content === this.endMarker) {
          shouldDeleteNode = false;
        }
      });
  },

  /**
   * createMarkerNode
   *
   * @param {string} nodeName
   * @param {Array<{ name: string, value: string }>} nodeAttributes
   * @returns {Node}
   */
  createMarkerNode(
    nodeName: string,
    nodeAttributes: Array<{ name: string, value: string }> = [],
  ): Node {
    const element = document.createElement(nodeName);

    nodeAttributes.forEach(({ name, value }) => {
      element.setAttribute(name, value);
    });

    return element;
  },

  /**
   * normalizeData
   * @param {string} content
   * @returns {string}
   */
  normalizeData(content: string): string {
    // replace div tags wrapping the content which might be injected when rendering react component with dangerouslySetInnerHtml
    return typeof content === 'string' ? content.replace(/^<div[^>]*>|<\/div>$/g, '').trim() : '';
  },

  /**
   * parseHtml
   *
   * @param {content} string
   * @returns {NodeList<Node>}
   */
  parseHtml(content: string): NodeList<any> {
    return new DOMParser().parseFromString(content, 'text/html').querySelectorAll('head *');
  },
};
